import { CommonEvents, MessageEvents } from "../events/EventEnums";
import { CurrentJackpotValue } from "../models/JackpotCurValue";
import { JackpotHitData } from "../models/JackpotHitData";
import {
  JackpotHistoryAckMessage,
  JackpotHistoryMessage,
  JackpotHitAckMessage,
  MessageType,
  ParametersAckMessage,
  ProgressiveCurValuesAckMessage,
  WebSocketMessage,
} from "../models/Messages";
import GlobalStore from "./GlobalStore";
import { WebSocketClient } from "./WebSocketClient";

export class WebSocketMessageHandler {
  private readonly wsClient: WebSocketClient;
  private checkInterval: number = 10000;
  private checkConnectionTimer: number = 0;
  private lastMessageTime: number = Date.now();
  private checkedTimes: number = 0;

  constructor(wsClient: WebSocketClient) {
    this.wsClient = wsClient;

    this.setTimer();
  }

  public handle = (data: string | object) => {
    if (!isObject(data) || !isWebSocketMessage(data)) {
      if (!(typeof data === "string" && data.startsWith("echo - "))) {
        console.error("Unexpected websocket message");
      }
      return;
    }

    const msgEE = GlobalStore.messageEventEmitter;
    switch (data.msgType) {
      case MessageType.PARAMETERS: {
        msgEE.emit(MessageEvents.MESSAGE_PARAMETERS, data);
        const acknowledge: ParametersAckMessage = {
          msgType: MessageType.PARAMETERS_ACK,
          eventId: data.eventId,
          signId: GlobalStore.getSignId(),
        };
        this.wsClient.send(acknowledge);
        break;
      }
      case MessageType.JACKPOT_HISTORY: {
        const newData: JackpotHitData[] = [];
        for (const h of (data as JackpotHistoryMessage).values) {
          newData.push({
            jackpot: h.jp,
            name: h.name,
            machineId: h.mach_id,
            timestamp: new Date(h.timestamp),
            value: h.value / 100,
            location: h.location,
          });
        }
        msgEE.emit(MessageEvents.MESSAGE_JACKPOT_HISTORY, newData);

        const acknowledge: JackpotHistoryAckMessage = {
          msgType: MessageType.JACKPOT_HISTORY_ACK,
          eventId: data.eventId,
          signId: GlobalStore.getSignId(),
        };
        this.wsClient.send(acknowledge);
        break;
      }
      case MessageType.JACKPOT_HIT: {
        if (!GlobalStore.isJackpotActive(data.params.jp)) {
          console.log(
            `Jackpot with ID '${data.params.jp}' no active. Skipping.`,
          );
          break;
        }

        const newData: JackpotHitData = {
          jackpot: data.params.jp,
          name: data.params.name,
          machineId: data.params.mach_id,
          timestamp: new Date(data.params.timestamp),
          value: data.params.value / 100,
          location: data.params.location ?? "",
        };
        msgEE.emit(MessageEvents.MESSAGE_JACKPOT_HIT, newData);

        const acknowledge: JackpotHitAckMessage = {
          msgType: MessageType.JACKPOT_HIT_ACK,
          eventId: data.eventId,
          signId: GlobalStore.getSignId(),
        };
        this.wsClient.send(acknowledge);
        break;
      }
      case MessageType.PROGRESSIVE_CUR_VALUES: {
        const newData: CurrentJackpotValue[] = [];
        for (const val of data.values) {
          newData.push({
            code: val.code,
            value: val.value / 100,
          });
        }
        msgEE.emit(MessageEvents.MESSAGE_PROGRESSIVE_CUR_VALUES, newData);

        const acknowledge: ProgressiveCurValuesAckMessage = {
          msgType: MessageType.PROGRESSIVE_CUR_VALUES_ACK,
          timestamp: new Date().getTime(),
          signId: GlobalStore.getSignId(),
        };
        this.wsClient.send(acknowledge);
        break;
      }
    }

    this.lastMessageTime = Date.now();
    this.setTimer();
  };

  private setTimer = () => {
    if (this.checkConnectionTimer > 0) {
      clearTimeout(this.checkConnectionTimer);
      this.checkConnectionTimer = 0;
      if (this.checkedTimes > 0) {
        GlobalStore.messageEventEmitter.emit(
          MessageEvents.MESSAGE_CONNECTION_OK,
        );
      }
      this.checkedTimes = 0;
      console.log("Check timer reset");
    }

    this.checkConnectionTimer = setTimeout(() => {
      this.checkedTimes++;
      console.log("Checked timer", this.checkedTimes);
      this.checkConnectionTimer = 0;
      if (this.checkedTimes === 1) {
        GlobalStore.messageEventEmitter.emit(
          MessageEvents.MESSAGE_CONNECTION_WARN,
        );
      } else if (this.checkedTimes > 1) {
        GlobalStore.messageEventEmitter.emit(
          MessageEvents.MESSAGE_CONNECTION_ERROR,
        );
      }
      this.setTimer();
    }, this.checkInterval);
  };

  private checkConnection = () => {};
}

function isObject(o: any): o is Record<string, any> {
  return o !== null && typeof o === "object";
}

function isWebSocketMessage(obj: any): obj is WebSocketMessage {
  return isObject(obj) && typeof obj.msgType === "number";
}
